import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    BreadcrumbNavigationService,
    ConfigurationService,
    PortalConfigurationService,
    PortalPreferencesInterface,
    TrackingV2Service
} from '@draeger-academy/common';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';

@Component({
    selector: 'da-portal-legal-page',
    templateUrl: './portal-legal-page.component.html',
    styleUrls: ['./portal-legal-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PortalLegalPageComponent implements OnInit, OnDestroy {
    public errorMessage: string;
    public portal: string;
    private _$portalPreferences: BehaviorSubject<PortalPreferencesInterface> = new BehaviorSubject<PortalPreferencesInterface>(null);
    private _$loading: BehaviorSubject<boolean> = new BehaviorSubject<any>(true);
    private _$json: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private _$portalId: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private _$headline: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    private _fileContext: string;
    private _pageType: string;
    private _availableFiles: any;
    private langChangeSubscription: Subscription;
    private translateStream: Subscription;

    constructor(
        private _portalConfigurationService: PortalConfigurationService,
        private _configurationService: ConfigurationService,
        private _localizeRouterService: LocalizeRouterService,
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _breadcrumbNavigationService: BreadcrumbNavigationService,
        private _translateService: TranslateService,
        private _trackingV2Service: TrackingV2Service
    ) {}

    public get loading$(): Observable<any> {
        return this._$loading.asObservable();
    }

    public get headline$(): Observable<string> {
        return this._$headline.asObservable();
    }

    public get json$(): Observable<any> {
        return this._$json.asObservable();
    }

    public get portalId$(): Observable<any> {
        return this._$portalId.asObservable();
    }

    ngOnInit(): void {
        if (this._configurationService.isPartnerPage()) {
            this._pageType = this._activatedRoute.snapshot.routeConfig.path;
            this._fileContext = this._getPageInfos('context');
            this._configurationService.currentPortal$.subscribe((portalId) => {
                if (portalId) {
                    this._$portalId.next(portalId);
                    this.portal = portalId;
                    this._trackPage();
                }
            });

            this._portalConfigurationService.loadPortalPreferences(this._fileContext).then((availableFiles) => {
                this._availableFiles = availableFiles;
                if (!this._$portalId.getValue()) {
                    this.errorMessage = 'No portal-configuration found (no Partner-ID).';
                    this._$loading.next(false);
                } else {
                    this._loadPortalPreferences().then(() => {
                        window.scrollTo(0, 0);
                        this._$loading.next(false);

                        this.langChangeSubscription = this._translateService.onLangChange.subscribe((event: LangChangeEvent) => {
                            if (this.translateStream) {
                                this.translateStream.unsubscribe();
                            }
                            this._loadLegalFile();
                            this._setBreadcrumbAndHeadline();
                        });
                    });
                }
            });
        }
    }

    private _trackPage() {
        this._trackingV2Service.loadTrackingScript();
        this._trackingV2Service.trackPage({
            template: 'LegalNoticePage',
            title: 'Dräger Academy Partnerportal ' + this.portal + ' | Legal Notices'
        });
    }

    public ngOnDestroy(): void {
        if (this.translateStream) {
            this.translateStream.unsubscribe();
        }
        if (this.langChangeSubscription) {
            this.langChangeSubscription.unsubscribe();
        }
        this._breadcrumbNavigationService.resetBreadcrumb();
    }

    private _loadPortalPreferences(): Promise<any> {
        return this._portalConfigurationService.loadPortalPreferences(this._$portalId.getValue()).then((preferences) => {
            this._$portalPreferences.next(preferences);
            if (this._pageType.toLowerCase() === 'revocation' && !preferences.showRevocation) {
                const translatedPath: any = this._localizeRouterService.translateRoute('/' + 'page-not-found');
                this._router.navigate([translatedPath], { skipLocationChange: true });
            }

            this._loadLegalFile();
            this._setBreadcrumbAndHeadline();
        });
    }

    private _loadLegalFile() {
        const _legalFile = this._getFileName();

        this._portalConfigurationService.loadLegalfile(_legalFile, this._fileContext).then((json) => {
            const _cleanJSON = this._portalConfigurationService.cleanUpJson(json);
            this._$json.next(_cleanJSON);
        });
    }

    private _getFileName(): string {
        const _mapPartnerContractCode = this._portalConfigurationService.contractCodeMapping;
        const _lang = this._portalConfigurationService.getPortalLanguage(this._$portalPreferences.getValue());
        const _prefix = this._getPageInfos('filePrefix');
        const _partnerContractCode = _mapPartnerContractCode.partnerContractCode[this._$portalId.getValue()]
            ? _mapPartnerContractCode.partnerContractCode[this._$portalId.getValue()]
            : 'DEFAULT';
        let _fileName = _prefix + '' + _partnerContractCode + '' + '.' + _lang.toUpperCase();
        const fileFromPortal = this._availableFiles.portalFileNames.filter((fileName) => {
            return fileName.name.toLowerCase().indexOf(_fileName.toLowerCase()) >= 0;
        });

        if (!fileFromPortal || fileFromPortal.length === 0) {
            _fileName = _prefix + 'DEFAULT' + '.' + _lang.toUpperCase();
        }
        return _fileName;
    }

    private _setBreadcrumbAndHeadline() {
        const _lang = this._portalConfigurationService.getPortalLanguage(this._$portalPreferences.getValue());
        const _breadcrumbLabel = this._getPageInfos('breadcrumbProperty');

        this.translateStream = this._translateService.stream('common').subscribe((data: any) => {
            if (typeof data !== 'string') {
                this._$headline.next(data[_breadcrumbLabel]);
                this._breadcrumbNavigationService.initPartnerLegalBreadcrumb(this._$portalId.getValue(), {
                    label: data[_breadcrumbLabel],
                    link: this._pageType.toLowerCase()
                });
            }
        });
    }

    private _getPageInfos(property: string) {
        return this._portalConfigurationService.getPortalInfoByPageType(this._pageType.toLowerCase(), property);
    }
}
